import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import * as Sentry from '@sentry/react'
import {
  applyThemeTokens,
  assertUiConfig,
  FALLBACK_UI_CONFIG_ID,
  getActiveUiConfig,
  InvalidUiConfigError,
  LOGIN_FALLBACK_UI_CONFIG,
  mergeClientNameFromApiEnvelope,
  setActiveUiConfig,
} from './uiConfig'

/** Used to avoid race: only one of callback vs UiConfigProvider should fetch at a time. */
let epicUiConfigLoadInProgress = false

export function isEpicUiConfigLoadInProgress(): boolean {
  return epicUiConfigLoadInProgress
}

export function setEpicUiConfigLoadInProgress(value: boolean): void {
  epicUiConfigLoadInProgress = value
}

/**
 * Load client UI configuration from provider-api for the Epic flow.
 *
 * Call this only after Epic token exchange (retrieveSmartOnFHIRAccessContext)
 * has completed so :class:`ProviderAPIClient` can use the session's provider-api
 * JWT. On success, validates the payload via :func:`assertUiConfig` (no merging
 * with defaults) and sets it as the active config. On failure, applies
 * :data:`LOGIN_FALLBACK_UI_CONFIG` so the public/login surface remains usable.
 *
 * @param orgId IMDB organization UUID — must match
 *   ``https://inflectionmedicine.com/org_id`` from epic-provision (path segment).
 * @param fhirServerUrl Optional FHIR resource server base URL (SMART ``iss``);
 *   when set, provider-api resolves config via microapp ``fhirServerUrl`` lookup.
 */
export async function loadEpicUiConfig(orgId: string, fhirServerUrl?: string | null): Promise<void> {
  // Avoid race with UiConfigProvider: skip if a real config is already loaded
  // or another loader is in flight.
  const existing = getActiveUiConfig()
  if (existing && existing.id !== FALLBACK_UI_CONFIG_ID) {
    return
  }
  if (epicUiConfigLoadInProgress) {
    return
  }

  epicUiConfigLoadInProgress = true
  try {
    const client = ProviderAPIClient.getInstance()
    const q = fhirServerUrl && fhirServerUrl.trim() ? `?fhirServerUrl=${encodeURIComponent(fhirServerUrl.trim())}` : ''
    const response = await client.get(`/config/ui/${encodeURIComponent(orgId)}${q}`, {
      Accept: 'application/json',
    })

    if (!response.ok) {
      const errorMessage = `Failed to load Epic UI config: ${response.status} for orgId ${orgId}`
      console.error('[loadEpicUiConfig]', errorMessage)
      Sentry.captureException(new Error(errorMessage), {
        tags: { operation: 'ui_config.load', flow: 'epic' },
        extra: { orgId, status: response.status },
      })
      applyThemeTokens(LOGIN_FALLBACK_UI_CONFIG.themeTokens)
      setActiveUiConfig(LOGIN_FALLBACK_UI_CONFIG)
      return
    }

    const responseData = (await response.json()) as { ui_config?: unknown; client_name?: string | null }
    const config = mergeClientNameFromApiEnvelope(assertUiConfig(responseData.ui_config), responseData.client_name)
    applyThemeTokens(config.themeTokens)
    setActiveUiConfig(config)
  } catch (error) {
    const message =
      error instanceof InvalidUiConfigError
        ? `Invalid Epic UI config payload: ${error.message}`
        : 'Epic UI config load failed'
    console.error('[loadEpicUiConfig]', message, error)
    Sentry.captureException(error, {
      tags: { operation: 'ui_config.load', flow: 'epic' },
      extra: { orgId },
    })
    applyThemeTokens(LOGIN_FALLBACK_UI_CONFIG.themeTokens)
    setActiveUiConfig(LOGIN_FALLBACK_UI_CONFIG)
  } finally {
    epicUiConfigLoadInProgress = false
  }
}
